import { Code, ContentCopy, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { StyledCode } from 'globalStyles';
import Button from 'material/components/Button';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import WidgetDialog from 'routes/ActionCenterDetails/components/WidgetDialog';
import { setSnackbarError } from 'store/actions/global';
import { useCopyToClipboard } from 'utils/hooks';
import {
  ActionButtonsWrapper,
  ActionCenterListWidgetCheckboxWrapper,
  InputsRow,
  InputWrapper,
  OptionsSection,
  OptionsToggle,
  PreviewWrapper,
  StyledCard,
  StyledFormControl,
  WidgetDescription,
  WidgetTitle,
} from './styles';

const ActionCenterListWidget = ({ reportsSchema }) => {
  const dispatch = useDispatch();
  const [copy] = useCopyToClipboard();
  const { organizationUuid } = useParams();
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [width, setWidth] = useState('100');
  const [height, setHeight] = useState('1000');
  const [fullHeight, setFullHeight] = useState(false);
  const [customCssUrl, setCustomCssUrl] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const getWidgetCode = () => {
    const { REACT_APP_LANDING_PAGE_URL, REACT_APP_LIST_WIDGET_URL } = process.env;

    const dataCampaign = selectedCampaign ? `\n      data-campaign="${selectedCampaign}"` : '';

    return `
    <div
      id="muster-list"
      class="muster-embed muster-embed-list"${dataCampaign}
      data-org-id="${organizationUuid}"
      data-domain="${REACT_APP_LANDING_PAGE_URL || 'https://mstr.app'}"
      data-height="${height}"
      data-width="${width}%"
      data-height-full="${fullHeight ? 'true' : 'false'}"
      data-css="${customCssUrl}"
    >
      <script src="${REACT_APP_LIST_WIDGET_URL || 'https://widgets.muster.com/v2/list-action-centers.js'}"></script>
    </div>
    `;
  };

  const validate = () => {
    if (customCssUrl && (!customCssUrl.startsWith('https') || !customCssUrl.endsWith('.css'))) {
      dispatch(
        setSnackbarError({ message: 'Custom CSS URL must start with https and end with .css' })
      );

      return false;
    }

    return true;
  };

  const handleCopyToClipboard = () => {
    if (!validate()) return;

    copy(getWidgetCode(), 'textarea', 'Code');
  };

  const handleViewCode = () => {
    if (!validate()) return;

    setIsDialogOpen(true);
  };

  const handleDialogCopyToClipboard = () => {
    copy(getWidgetCode(), 'textarea', 'Code');
    setIsDialogOpen(false);
  };

  return (
    <>
      <StyledCard>
        <WidgetTitle variant="h6">Action Center List Widget</WidgetTitle>
        <WidgetDescription variant="body2">
          List active action centers on your website. Optionally filter by a campaign.
        </WidgetDescription>

        <PreviewWrapper>
          <img
            alt="Action center widget preview"
            height="200px"
            src="/images/widget-preview-action-center-listing.webp"
          />
        </PreviewWrapper>

        <OptionsToggle onClick={handleOptionsToggle}>
          {showOptions ? 'Hide options' : 'Options'}
          {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </OptionsToggle>

        {showOptions && (
          <OptionsSection>
            <InputsRow>
              <InputWrapper>
                <StyledFormControl variant="outlined">
                  <InputLabel htmlFor="campaign-widget-select" sx={{ backgroundColor: 'white' }}>
                    Campaign (Optional)
                  </InputLabel>
                  <Select
                    variant="outlined"
                    native
                    value={selectedCampaign}
                    onChange={e => setSelectedCampaign(e.target.value)}
                    label="Campaign"
                    inputProps={{
                      id: 'campaign-widget-select',
                      name: 'campaign',
                    }}
                  >
                    <option value="ALL">All Campaigns</option>
                    {reportsSchema?.campaigns?.map(campaign => (
                      <option key={campaign.uuid} value={campaign.uuid}>
                        {campaign.name}
                      </option>
                    ))}
                  </Select>
                </StyledFormControl>
              </InputWrapper>
            </InputsRow>

            <InputsRow>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Width (%)"
                  value={width}
                  onChange={e => setWidth(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-width`"
                />
              </InputWrapper>
              <InputWrapper>
                <TextField
                  fullWidth
                  label="Height (pixels)"
                  value={height}
                  onChange={e => setHeight(e.target.value)}
                  variant="outlined"
                  size="medium"
                  helperText="Populates `data-height`"
                />
              </InputWrapper>
            </InputsRow>

            <ActionCenterListWidgetCheckboxWrapper>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fullHeight}
                    onChange={e => setFullHeight(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="body2">Full height</Typography>}
              />
              <FormHelperText
                sx={{
                  marginLeft: '14px',
                  marginRight: '14px',
                  marginTop: '3px',
                }}
              >
                Populates `data-height-full`
              </FormHelperText>
            </ActionCenterListWidgetCheckboxWrapper>

            <InputWrapper>
              <TextField
                fullWidth
                label="Custom CSS (Optional)"
                value={customCssUrl}
                onChange={e => setCustomCssUrl(e.target.value)}
                variant="outlined"
                size="medium"
                placeholder="https://example.com/styles.css"
              />
            </InputWrapper>
          </OptionsSection>
        )}

        <ActionButtonsWrapper>
          <Button variant="text" startIcon={<ContentCopy />} onClick={handleCopyToClipboard}>
            Copy to Clipboard
          </Button>
          <Button variant="contained" startIcon={<Code />} onClick={handleViewCode}>
            View Code
          </Button>
        </ActionButtonsWrapper>
      </StyledCard>
      <WidgetDialog
        isDialogOpen={isDialogOpen}
        handleDialogClose={() => setIsDialogOpen(false)}
        title="Widget Code"
        handleCopyToClipboard={handleDialogCopyToClipboard}
      >
        <StyledCode>{getWidgetCode()}</StyledCode>
      </WidgetDialog>
    </>
  );
};

ActionCenterListWidget.propTypes = {
  reportsSchema: PropTypes.shape({
    campaigns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        uuid: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default ActionCenterListWidget;
